import './credit-application.css';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useLocation, useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';

import { useAppContext } from '../../utils/content.util';
import {
  ConsentSchema,
  SaleDetailsSchema,
  ApplicantInformationSchema,
  EmploymentInformationSchema,
  ResidentialAddressSchema,
  CoapplicantInformationSchema,
  CoapplicantResidentiaAddressSchema,
  CoapplicantEmploymentInformationSchema,
} from './validationSchema';
import {
  ApplicantInformationForm,
  initialValuesApplicantInformation,
} from '../../components/credit-application/step-1-applicant-information.component';
import {
  ResidentialAddressForm,
  initialValuesResidentialAddress,
} from '../../components/credit-application/step-2-residential-address.component';
import {
  EmploymentInformationForm,
  initialValuesEmploymentInformation,
} from '../../components/credit-application/step-3-employment-information.component';
import {
  CoapplicantInformationForm,
  initialValuesStepCoapplicantInformation,
} from '../../components/credit-application/step-4-coapplicant-information.component';
import {
  SaleDetailsForm,
  initialValuesSaleDetails,
} from '../../components/credit-application/step-7-sale-details.component';
import { ConsentForm, initialValuesConsent } from '../../components/credit-application/step-8-consent.component';
import { createApplication, ValidationError } from '../../services/applications.service';
import { getDealer } from '../../services/dealers.service';
import { Dealer } from '../../models/dealer.model';
import { onError } from '../../utils/error.util';
import { CreateApplicationRequest } from '../../models/application.model';
import { CircularProgressComponent } from '../../components/admin/circular-progress.component';
import CustomBreadcrumb, { Step } from '../../components/breadcrumbs/breadcrumbs.component';
import {
  CoapplicantResidentialAddressForm,
  initialValuesCoapplicantResidentialAddress,
} from '../../components/credit-application/step-5-coapplicant-residential-address.component';
import {
  CoapplicantEmploymentInformationForm,
  initialValuesCoapplicantEmploymentInformation,
} from '../../components/credit-application/step-6-coapplicant-employment-information.component';
import { InputBox } from '../../components/form-elements/inputbox.component';
import { InputBoxPhone } from '../../components/form-elements/inputbox-phone.component';
import { CompanyConfig } from '../../company.config';
import { isDev } from '../../utils/utils';
import { LeadSource } from '../../models/application.model';
import { AwsCognitoUser } from '../../models/user.model';
import { POSITION_OPTIONS } from '../../models/role.model';

declare global {
  interface Window {
    handleCustomFieldChange?: (name: string, value: string) => void;
  }
}

interface CreditApplicationFormV2Props {
  leadSource: LeadSource;
}

const getAnalystName = (currentUser: AwsCognitoUser | undefined) => {
  if (!currentUser) {
    return '';
  }

  // Only publish name if user is an Analyst
  const position = currentUser['custom:position'];
  if (position !== POSITION_OPTIONS.analyst) {
    return '';
  }

  const fullName = `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim();
  return fullName || '';
};

const getCreatedBy = (currentUser: AwsCognitoUser | undefined) => {
  if (!currentUser) {
    return '';
  }

  const fullName = `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim();
  return fullName || '';
};

export default function CreditApplicationFormV2({ leadSource }: CreditApplicationFormV2Props) {
  const { currentUser, isAuthenticated } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = location.pathname === '/phone';
  const isEmbedded = location.pathname.startsWith('/embed');

  // params
  const { dealerId, dealerLocation, dealerStock, analyticName } = useParams();

  // search params
  const [searchParams] = useSearchParams();
  const fname = searchParams.get('fname') ?? '';
  const lname = searchParams.get('lname') ?? '';
  const email = searchParams.get('email') ?? '';
  const vehicleYear = searchParams.get('vyr') ?? '';
  const vehicleMake = searchParams.get('vmk') ?? '';
  const vehicleModel = searchParams.get('vmd') ?? '';
  const queryStock = searchParams.get('stock') ?? '';
  const queryLocation = searchParams.get('location') ?? '';

  const MAX_FORM_STEP = 8;
  const STEPS: Step[] = useMemo(
    () => [
      {
        stepNumber: 1, // FIXME: use the index
        label: 'Buyer <span class="indicator-break">Information</span>',
        labelNumber: 1,
        initialValues: {
          ...initialValuesApplicantInformation,
          firstName: fname,
          lastName: lname,
          emailAddress: email, //
        },
        validationSchema: ApplicantInformationSchema(isPhone),
      },
      {
        stepNumber: 2,
        label: 'Residential <span class="indicator-break">Information</span>',
        labelNumber: 2,
        initialValues: initialValuesResidentialAddress,
        validationSchema: ResidentialAddressSchema,
      },
      {
        stepNumber: 3,
        label: 'Employment <span class="indicator-break">Information</span>',
        labelNumber: 3,
        initialValues: initialValuesEmploymentInformation,
        validationSchema: EmploymentInformationSchema,
      },
      // Co-Buyer Information
      {
        stepNumber: 4,
        label: 'Co-Buyer <span class="indicator-break">Information</span>',
        labelNumber: 4,
        initialValues: initialValuesStepCoapplicantInformation,
        validationSchema: CoapplicantInformationSchema,
      },
      // Co-Buyer Residential Address
      {
        stepNumber: 5,
        label: 'Co-Buyer <span class="indicator-break">Residential Information</span>',
        labelNumber: 4,
        initialValues: initialValuesCoapplicantResidentialAddress,
        validationSchema: CoapplicantResidentiaAddressSchema,
      },
      // Co-Buyer Employment Information
      {
        stepNumber: 6,
        label: 'Co-Buyer <span class="indicator-break">Employment Information</span>',
        labelNumber: 4,
        initialValues: initialValuesCoapplicantEmploymentInformation,
        validationSchema: CoapplicantEmploymentInformationSchema,
      },
      // Sale Details
      {
        stepNumber: 7,
        label: 'Sale <span class="indicator-break">Details</span>',
        labelNumber: 5,
        initialValues: {
          ...initialValuesSaleDetails,
          vehicleYear,
          vehicleMake,
          vehicleModel, //
        },
        validationSchema: SaleDetailsSchema,
      },
      // Consent
      {
        stepNumber: 8,
        label: 'Consent',
        labelNumber: 6,
        initialValues: initialValuesConsent,
        validationSchema: ConsentSchema,
      },
    ],
    [fname, lname, email, vehicleYear, vehicleMake, vehicleModel, isPhone],
  );

  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  // const [formSteps, setFormSteps] = useState(STEPS);
  const [formSteps, setFormSteps] = useState(() => {
    const steps = [...STEPS];
    // Update initial values for first step if user is authenticated
    if (isAuthenticated && currentUser) {
      // const fullName = `${currentUser.given_name ?? ''} ${currentUser.family_name ?? ''}`.trim();
      steps[0].initialValues = {
        ...steps[0].initialValues,
        createdBy: getCreatedBy(currentUser),
      };
    }
    return steps;
  });
  const [formData, setFormData] = useState(() => {
    const data = { ...initialValues };
    // Set createdBy in formData if user is authenticated
    if (isAuthenticated && currentUser) {
      data.createdBy = getCreatedBy(currentUser);
    }
    return data;
  });
  const [isCreditAppSubmitting, setIsCreditAppSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  const [dealer, setDealer] = useState<Dealer | undefined>(undefined);
  const [partnerName, setPartnerName] = useState<string | undefined>(undefined);

  // set partner name - so custom - so hard coded for now
  useEffect(() => {
    setPartnerName(leadSource === LeadSource.EMBED_MAZO ? 'Mazo Capital Solutions' : '');
  }, [leadSource]);

  const [customAttributes, setCustomAttributes] = useState<Record<string, string>>({});

  const handlePrev = async (_values: any) => {
    if (currentStep === 7 && (formData.haveCoApplicant as any as boolean) === false) {
      // co-applicant - go to Coapplicant
      setCurrentStep(4);
    } else {
      if (currentStep > 0) {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleNext = async (values: any) => {
    setFormData({ ...formData, ...values });

    if (currentStep < MAX_FORM_STEP) {
      const newFormSteps = formSteps.map((s) => {
        if (s.stepNumber === currentStep) {
          return { ...s, initialValues: values };
        }
        return s;
      });
      setFormSteps(newFormSteps);

      if (currentStep === 4 && values.haveCoApplicant === false) {
        // co-applicant - go to SaleDetails
        setCurrentStep(7);
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      setIsCreditAppSubmitting(true);
      // refresh the recaptcha
      setRefreshReCaptcha((r) => !r);
    }
  };

  useEffect(() => {
    async function onLoad() {
      if (!dealerId) {
        setIsLoading(false);
        return;
      }
      // make sure the dealer id is a number
      if (isNaN(Number(dealerId))) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await getDealer(dealerId);
        setDealer(response);
        setPartnerName(response.companyName);
      } catch (err) {
        navigate('/');
        // onError(err);
      }

      setIsLoading(false);
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  function phoneApplication({ values, touched, errors, handleChange, handleBlur, setFieldValue }: any) {
    return (
      <Row>
        <Col xs={12} className="pb-3">
          <h4>Service Form Phone Submission</h4>
        </Col>

        <Col xs={12} md={6}>
          <InputBox
            disabled={true}
            label="Team Member"
            name="createdBy"
            type="text"
            placeholder="Full Name"
            value={values.createdBy}
            onChange={handleChange}
            onBlur={handleBlur}
            required={true}
            isValid={touched.createdBy && !errors.createdBy}
            isInvalid={touched.createdBy && !!errors.createdBy}
            errorFeedback={errors.createdBy}
          />
        </Col>

        <Col xs={12} md={6}>
          <InputBoxPhone
            label="Phone Number Callback"
            name="phoneNumberCallback"
            value={values.phoneNumberCallback}
            onChange={handleChange}
            onBlur={handleBlur}
            setFieldValue={setFieldValue}
            isValid={touched.phoneNumberCallback && !errors.phoneNumberCallback}
            isInvalid={touched.phoneNumberCallback && !!errors.phoneNumberCallback}
            errorFeedback={errors.phoneNumberCallback}
          />
        </Col>
      </Row>
    );
  }

  function embeddedApplication() {
    return (
      <div id="embedded-application-container">
        <Row className="my-3">
          <Col xs={12}>
            <img
              style={{
                display: 'block',
                width: '150px',
                margin: '15px 20px',
                padding: '0px',
                outline: '0',
                border: '0',
              }}
              alt={`${CompanyConfig.name} Logo`}
              src="https://www.trailersolutionsfl.com/images/tsf-logo-20-years-in-business.png"
            />

            <p
              style={{
                display: 'block',
                fontFamily: 'Montserrat',
                fontSize: '13px',
                lineHeight: '1.5',
                color: '#333',
                padding: '0px 15px',
              }}
            >
              <strong>
                This form is provided by {CompanyConfig.name} ({CompanyConfig.shortName}), a third-party financial
                services provider, in partnership with {partnerName}.
              </strong>
              <br />
              By submitting your information, you acknowledge that it will be sent directly to {CompanyConfig.name} for
              processing. {CompanyConfig.name} will share your information with its affiliated lenders for the purpose
              of evaluating financing options related to your inquiry. Submission of this form does <strong>not</strong>{' '}
              constitute a loan approval, or offer of credit. It is intended only to begin the process of exploring
              available financing. If you have any questions about how your information will be used, please read our{' '}
              <a
                style={{ color: '#162f5e', textDecoration: 'underline', outline: '0', border: '0' }}
                href={`${CompanyConfig.website}/privacy`}
                title={`${CompanyConfig.name} Privacy Policy`}
                target="_blank"
                rel="nofollow noreferrer"
              >
                Privacy Policy
              </a>{' '}
              &amp;{' '}
              <a
                style={{ color: '#162f5e', textDecoration: 'underline', outline: '0', border: '0' }}
                href={`${CompanyConfig.website}/terms-of-use`}
                title={`${CompanyConfig.name} Terms of Use`}
                target="_blank"
                rel="nofollw noreferrer"
              >
                Terms of Use
              </a>
              , or{' '}
              <a
                style={{ color: '#162f5e', textDecoration: 'underline', outline: '0', border: '0' }}
                href={`${CompanyConfig.website}/contact`}
                title={`Contact ${CompanyConfig.name}`}
                target="_blank"
                rel="nofollow noreferrer"
              >
                contact {CompanyConfig.name}
              </a>{' '}
              directly before submitting the form.
            </p>
          </Col>
        </Row>
      </div>
    );
  }

  useEffect(() => {
    if (dealer?.customThemeContent) {
      window.handleCustomFieldChange = function (name, value) {
        setCustomAttributes((prev) => ({
          ...prev,
          [name]: value,
        }));
      };
    }
  }, [dealer?.customThemeContent]);

  function customThemeContainer() {
    return dealer?.customThemeContent ? (
      <div id="custom-theme-content" dangerouslySetInnerHTML={{ __html: dealer.customThemeContent }} />
    ) : null;
  }

  function disclaimerContainer(dealer: Dealer | undefined) {
    return (
      <div id="dealer_info">
        <div className="container-xl">
          <div className="row">
            <p>
              <strong>
                This form is provided by {CompanyConfig.name} ({CompanyConfig.shortName}), a third-party financial
                services provider{dealer?.companyName ? `, in partnership with ${dealer?.companyName}` : ''}.
              </strong>
              <br />
              By submitting your information, you acknowledge that it will be sent directly to {
                CompanyConfig.shortName
              }{' '}
              for processing. {CompanyConfig.shortName} will share your information with its affiliated lenders for the
              purpose of evaluating financing options related to your inquiry. Submission of this form does not
              constitute a loan approval, or offer of credit. It is intended only to begin the process of exploring
              available financing. If you have any questions about how your information will be used, please read our{' '}
              <Link
                className="link"
                to={CompanyConfig.privacyUrl}
                title={`${CompanyConfig.name} Privacy Policy`}
                target="_blank"
              >
                Privacy Policy
              </Link>{' '}
              &{' '}
              <Link
                className="link"
                to={CompanyConfig.termsUrl}
                title={`${CompanyConfig.name} Terms of Use`}
                target="_blank"
              >
                Terms of Use
              </Link>
              , or{' '}
              <Link
                className="link"
                to={CompanyConfig.contactUrl}
                title={`Contact ${CompanyConfig.name}`}
                target="_blank"
              >
                contact {CompanyConfig.shortName}
              </Link>{' '}
              directly before submitting the form.
              <br />
              <strong>{CompanyConfig.name}</strong>- {CompanyConfig.address.addressLine1}, {CompanyConfig.address.city},{' '}
              {CompanyConfig.address.state} {CompanyConfig.address.zipCode}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const onVerify = useCallback(
    async (token: string) => {
      if (isCreditAppSubmitting) {
        try {
          const payload: CreateApplicationRequest = {
            ...formData,
            haveCoApplicant: (formData.haveCoApplicant as any as boolean) === true,
            dealerId: dealer?.id,
            dealerName: dealer?.companyName || formData.dealerName,
            dealerLocation: dealerLocation || queryLocation,
            dealerStock: dealerStock || queryStock,
            analyticName,
            token,
            customAttributes,
            leadSource,
            analystName: getAnalystName(currentUser),
          };

          await createApplication(payload);

          if (currentUser) {
            navigate('/service-forms');
          } else if (dealerId) {
            window.location.href = CompanyConfig.dealerFormUrl;
          } else {
            window.location.href = CompanyConfig.formSubmittedUrl;
          }
        } catch (err) {
          setIsCreditAppSubmitting(false);
          if (err instanceof ValidationError) {
            alert(err.errors.join('\n'));
          } else {
            onError(err);
            alert('Something wrong. Try to start over.');
          }
        } finally {
          // setIsCreditAppSubmitting(false); // because we redirect to another website
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCreditAppSubmitting],
  );

  return (
    <>
      {isLoading ? (
        <CircularProgressComponent />
      ) : (
        <div id="body-content">
          <div id="custom-theme-container">{customThemeContainer()}</div>

          <div id="creditapp">
            {disclaimerContainer(dealer)}

            {currentStep > 1 ? (
              <Row className="my-4">
                <Col xs={12}>
                  <CustomBreadcrumb steps={STEPS} currentStep={currentStep} />
                </Col>
              </Row>
            ) : null}

            {currentStep === 1 ? (
              <Formik
                initialValues={formSteps[0].initialValues}
                validationSchema={formSteps[0].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="ApplicantInformationForm">
                    {isPhone
                      ? phoneApplication({ values, touched, errors, handleChange, handleBlur, setFieldValue })
                      : undefined}

                    {isEmbedded ? embeddedApplication() : null}

                    <Row className="my-4">
                      <Col xs={12}>
                        <CustomBreadcrumb steps={STEPS} currentStep={currentStep} />
                      </Col>
                    </Row>

                    <ApplicantInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 2 ? (
              <Formik
                initialValues={formSteps[1].initialValues}
                validationSchema={formSteps[1].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="ResidentialAddressForm">
                    <ResidentialAddressForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 3 ? (
              <Formik
                initialValues={formSteps[2].initialValues}
                validationSchema={formSteps[2].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="EmploymentInformationForm">
                    <EmploymentInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 4 ? (
              <Formik
                initialValues={formSteps[3].initialValues}
                validationSchema={formSteps[3].validationSchema(formData.ssn, formData.birthdate)}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="CoapplicantInformationForm">
                    <CoapplicantInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 5 ? (
              <Formik
                initialValues={{
                  ...formSteps[4].initialValues,
                  haveCoApplicant: formData.haveCoApplicant,
                  // haveCoApplicant: true,
                  applicantResidentialAddressLine1: formData.residentialAddressLine1,
                  applicantResidentialCity: formData.residentialCity,
                  applicantResidentialState: formData.residentialState,
                  applicantResidentialZipCode: formData.residentialZipCode,
                }}
                validationSchema={formSteps[4].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="CoapplicantResidentialAddressForm">
                    <CoapplicantResidentialAddressForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 6 ? (
              <Formik
                initialValues={{
                  ...formSteps[5].initialValues,
                  haveCoApplicant: formData.haveCoApplicant, //
                  // haveCoApplicant: true,
                }}
                validationSchema={formSteps[5].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="CoapplicantEmploymentInformationForm">
                    <CoapplicantEmploymentInformationForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 7 ? (
              <Formik
                initialValues={{
                  ...formSteps[6].initialValues,
                  sellerPhoneNumber: dealer?.phone ?? '',
                    sellerContact: '',
                  dealerId: dealerId ?? '',
                  dealerName: dealer?.companyName ?? '',
                  referralSource: dealerId ? 'Dealership' : (formSteps[6].initialValues.referralSource ?? ''),
                }}
                validationSchema={formSteps[6].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="SaleDetailsForm">
                    <SaleDetailsForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                    />
                    <Row className="pt-3">
                      <Col xs={12}>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" className="bttn bttn-blue">
                          Next
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {currentStep === 8 ? (
              <Formik
                initialValues={{
                  ...formSteps[7].initialValues,
                  haveCoApplicant: formData.haveCoApplicant,
                  // haveCoApplicant: true,
                  firstName: formData.firstName,
                  lastName: formData.lastName,
                  // firstName: "John",
                  // lastName: "Doe",
                  coapplicantFirstName: formData.coapplicantFirstName,
                  coapplicantLastName: formData.coapplicantLastName,
                }}
                validationSchema={formSteps[7].validationSchema}
                onSubmit={(values) => handleNext(values)}
                enableReinitialize
              >
                {({ handleChange, handleBlur, values, touched, errors, setFieldValue }) => (
                  <Form noValidate id="ConsentForm">
                    <ConsentForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      isCustomTheme={dealer?.customThemeContent}
                      isEmbeddedMazo={leadSource === LeadSource.EMBED_MAZO}
                    />

                    <Row>
                      <Col xs={12}>
                        <GoogleReCaptcha
                          onVerify={onVerify}
                          refreshReCaptcha={refreshReCaptcha}
                          action="application__v2_submit"
                        />
                        <p style={{ fontSize: '16px', fontWeight: 'bold' }}>
                          You'll receive a confirmation email within 10 minutes of clicking "Submit". If you don't see
                          it, please check your spam folder. If it's still not there, give us a call at 800-224-8180,
                          and we'll be happy to assist you further.
                        </p>
                        <button onClick={handlePrev} className="mr-1 bttn bttn-blue">
                          Prev
                        </button>
                        <button type="submit" disabled={isCreditAppSubmitting} className="ml-2 bttn bttn-blue">
                          {!isCreditAppSubmitting ? (
                            'Submit'
                          ) : (
                            <React.Fragment>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                style={{ marginRight: '5px' }}
                              />
                              Submitting...
                            </React.Fragment>
                          )}
                        </button>
                      </Col>
                    </Row>

                    {isDev ? (
                      <>
                        <div>
                          <p>Values:</p>
                          <code>{JSON.stringify(values, null, 2)}</code>
                        </div>
                        <div>
                          <p>Errors:</p>
                          <code>{JSON.stringify(errors, null, 2)}</code>
                        </div>
                      </>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : null}

            {isDev ? (
              <div>
                <p>All Values:</p>
                <code>{JSON.stringify(formData, null, 2)}</code>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}

const initialValues = {
  ...initialValuesApplicantInformation, // 1
  ...initialValuesResidentialAddress, // 2
  ...initialValuesEmploymentInformation, // 3
  ...initialValuesStepCoapplicantInformation, // 4
  ...initialValuesCoapplicantResidentialAddress, // 5
  ...initialValuesCoapplicantEmploymentInformation, // 6
  ...initialValuesSaleDetails, // 7
  ...initialValuesConsent, // 8
  customAttributes: {},
};
